// ============================================================================
// AccueilTeamKpis — bande KPI discrete de Mon equipe (TEAM-KPI-WEIGHT)
// ============================================================================
// Une rangee basse dans une seule carte, partagee par l'onglet de l'accueil et
// la page /mon-equipe (lot design-mon-equipe-en-une-page). Echelle nommee
// text-body font-semibold — pas de text-kpi font-bold.

import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Users, Wallet, Activity, BellRing } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { TeamPayrollBase } from '../../hooks/useMonEquipe';

const ITEM_CLASS = 'flex min-w-0 items-center gap-3 px-4 py-2.5';

function DiscreteKpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
}) {
  return (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
        <Icon size={16} className="text-slate-600 dark:text-slate-300" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-meta font-medium text-slate-500 dark:text-slate-400">{label}</span>
        <span className="block truncate text-body font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {value}
        </span>
      </span>
    </>
  );
}

function formatMoney(locale: string, value: number): string {
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function AccueilTeamKpis({
  locale,
  headcount,
  submittedCount,
  payroll,
  openReminders,
  payrollTo,
}: {
  locale: string;
  headcount: number;
  submittedCount: number;
  payroll: TeamPayrollBase | null;
  openReminders: number;
  /** Quand fourni, la masse salariale (agregat M-153) mene a la planification. */
  payrollTo?: string;
}) {
  const { t } = useTranslation();

  let payrollValue = '—';
  if (payroll) {
    payrollValue = formatMoney(locale, payroll.total);
    if (payroll.unpricedCount > 0) {
      payrollValue = t('gest:accueil.teamView.kpis.payrollWithUnpriced', {
        amount: formatMoney(locale, payroll.total),
        count: payroll.unpricedCount,
      });
    }
  }

  return (
    <div
      className={`${GS_COMPONENTS.card} grid grid-cols-2 divide-slate-100 dark:divide-slate-700 xl:grid-cols-4 xl:divide-x`}
    >
      <div className={ITEM_CLASS}>
        <DiscreteKpi icon={Users} label={t('gest:monEquipe.kpis.headcount')} value={String(headcount)} />
      </div>
      <div className={ITEM_CLASS}>
        <DiscreteKpi
          icon={Activity}
          label={t('gest:monEquipe.kpis.surveys')}
          value={`${submittedCount}/${headcount}`}
        />
      </div>
      {payrollTo ? (
        <Link
          to={payrollTo}
          className={`${ITEM_CLASS} touch-manipulation rounded-xl transition-colors hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-slate-700/40`}
        >
          <DiscreteKpi icon={Wallet} label={t('gest:monEquipe.kpis.payroll')} value={payrollValue} />
        </Link>
      ) : (
        <div className={ITEM_CLASS}>
          <DiscreteKpi icon={Wallet} label={t('gest:monEquipe.kpis.payroll')} value={payrollValue} />
        </div>
      )}
      <div className={ITEM_CLASS}>
        <DiscreteKpi icon={BellRing} label={t('gest:monEquipe.kpis.reminders')} value={String(openReminders)} />
      </div>
    </div>
  );
}
